
import { Bell, Calendar, UserPlus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTranslations } from "@/hooks/useTranslations";
import { useLanguage } from "@/components/LanguageContext";
import { Link } from "react-router-dom";
import { useLanguagePath } from "@/utils/language";
import { useEffect } from "react";

type NotificationsProps = {
  userId: string | undefined;
};

export const DashboardNotifications = ({ userId }: NotificationsProps) => {
  const { t } = useTranslations("dashboard");
  const { language } = useLanguage();
  const { getPath } = useLanguagePath();
  const dateLocale = language === 'pt' ? ptBR : undefined;

  const { data: notifications, refetch } = useQuery({
    queryKey: ["trainer-notifications", userId],
    queryFn: async () => {
      if (!userId) return {
        classRequests: [],
        studentRequests: [],
        cancelRequests: []
      };
      
      const [classRequests, studentRequests, cancelRequests] = await Promise.all([
        supabase.from("schedule_requests").select(`
          id,
          created_at,
          requested_date,
          student:students!schedule_requests_student_id_fkey (
            first_name,
            last_name
          )
        `).eq("trainer_id", userId).eq("status", "pending"),
        
        supabase.from("student_requests").select("*").eq("trainer_id", userId).eq("status", "pending"),
        
        supabase.from("schedule_events").select(`
          id,
          created_at,
          student:students!schedule_events_student_id_fkey (
            first_name,
            last_name
          )
        `).eq("trainer_id", userId).eq("status", "cancelled").not("cancellation_reason", "is", null)
      ]);
      
      return {
        classRequests: classRequests.data || [],
        studentRequests: studentRequests.data || [],
        cancelRequests: cancelRequests.data || []
      };
    },
    enabled: !!userId,
    refetchInterval: 30000
  });

  // Setup realtime subscriptions
  useEffect(() => {
    if (!userId) return;

    const channel = supabase.channel('notifications')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'schedule_requests',
        filter: `trainer_id=eq.${userId}`
      }, () => {
        refetch();
      })
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'student_requests',
        filter: `trainer_id=eq.${userId}`
      }, () => {
        refetch();
      })
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'schedule_events',
        filter: `trainer_id=eq.${userId}`
      }, () => {
        refetch();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, refetch]);

  const totalNotifications = (notifications?.classRequests.length || 0) + 
                           (notifications?.studentRequests.length || 0) + 
                           (notifications?.cancelRequests.length || 0);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5 text-slate-gray-dark" />
          {notifications && totalNotifications > 0 && (
            <span className="absolute -top-1 -right-1 h-4 w-4 text-[10px] flex items-center justify-center bg-flame text-baby-powder rounded-full">
              {totalNotifications}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0 bg-white dark:bg-gray-800 border border-slate-gray-light/10 dark:border-gray-700 shadow-ios rounded-xl z-[100]" align="end">
        <div className="p-2 space-y-2 max-h-[60vh] overflow-y-auto">
          <h3 className="text-sm font-medium text-slate-800 dark:text-slate-200 border-b pb-2 dark:border-gray-700">
            {t("header.notifications.title")}
          </h3>
          
          {!notifications || totalNotifications === 0 ? (
            <p className="text-xs text-slate-gray-light dark:text-gray-400 text-center p-2">
              {t("header.notifications.noNotifications")}
            </p>
          ) : (
            <>
              {notifications.classRequests.map(request => (
                <Link
                  key={request.id}
                  to={getPath("/dashboard/schedule")}
                  className="block hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors rounded-lg group"
                >
                  <div className="flex items-start gap-2 text-xs border-b border-slate-gray-light/10 dark:border-gray-700/50 p-2">
                    <Calendar className="h-3 w-3 text-flame mt-0.5 flex-shrink-0 group-hover:text-secondary-purple" />
                    <div>
                      <p className="dark:text-gray-200 group-hover:text-secondary-purple transition-colors">
                        {t("header.notifications.newClassRequest")} <span className="font-medium">{request.student.first_name} {request.student.last_name}</span>
                      </p>
                      <p className="text-[10px] text-slate-gray-light dark:text-gray-400 group-hover:text-tertiary-purple transition-colors">
                        {format(new Date(request.created_at), "dd/MM/yyyy", {
                          locale: dateLocale
                        })} {t("header.notifications.at")} {format(new Date(request.created_at), "HH:mm", {
                          locale: dateLocale
                        })}
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
              {notifications.studentRequests.map(request => (
                <Link
                  key={request.id}
                  to={getPath("/dashboard/students")}
                  className="block hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors rounded-lg group"
                >
                  <div className="flex items-start gap-2 text-xs border-b border-slate-gray-light/10 dark:border-gray-700/50 p-2">
                    <UserPlus className="h-3 w-3 text-green-500 mt-0.5 flex-shrink-0 group-hover:text-secondary-purple" />
                    <div>
                      <p className="dark:text-gray-200 group-hover:text-secondary-purple transition-colors">
                        {t("header.notifications.newStudentRequest")} <span className="font-medium">{request.first_name} {request.last_name}</span>
                      </p>
                      <p className="text-[10px] text-slate-gray-light dark:text-gray-400 group-hover:text-tertiary-purple transition-colors">
                        {format(new Date(request.created_at), "dd/MM/yyyy", {
                          locale: dateLocale
                        })} {t("header.notifications.at")} {format(new Date(request.created_at), "HH:mm", {
                          locale: dateLocale
                        })}
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
              {notifications.cancelRequests.map(request => (
                <Link
                  key={request.id}
                  to={getPath("/dashboard/schedule")}
                  className="block hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors rounded-lg group"
                >
                  <div className="flex items-start gap-2 text-xs border-b border-slate-gray-light/10 dark:border-gray-700/50 p-2">
                    <X className="h-3 w-3 text-red-500 mt-0.5 flex-shrink-0 group-hover:text-secondary-purple" />
                    <div>
                      <p className="dark:text-gray-200 group-hover:text-secondary-purple transition-colors">
                        {t("header.notifications.cancellationRequest")} <span className="font-medium">{request.student.first_name} {request.student.last_name}</span>
                      </p>
                      <p className="text-[10px] text-slate-gray-light dark:text-gray-400 group-hover:text-tertiary-purple transition-colors">
                        {format(new Date(request.created_at), "dd/MM/yyyy", {
                          locale: dateLocale
                        })} {t("header.notifications.at")} {format(new Date(request.created_at), "HH:mm", {
                          locale: dateLocale
                        })}
                      </p>
                    </div>
                  </div>
                </Link>
              ))}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
